import classNames from "classnames"
import type {
  ComponentPropsWithoutRef,
  Ref,
} from "react"
import {
  forwardRef,
  useMemo,
  useState,
} from "react"

import type { SizePresetKey } from "./SizePresetKey"
import styles from "./SliderValue.module.scss"
import { SliderValueContext } from "./SliderValueContext"

enum AlignmentPreset {
  start = "sliderValueStartAlignment",
  center = "sliderValueCenterAlignment",
  end = "sliderValueEndAlignment",
}

enum SizePreset {
  small = "sliderValueSmall",
  large = "sliderValueLarge",
  largePopover = "sliderValueLargePopover",
}

type AlignmentPresetKey = keyof typeof AlignmentPreset
export const defaultAlignmentPreset: AlignmentPresetKey = "start"

export const defaultSizePreset: SizePresetKey = "small"

export interface Props extends ComponentPropsWithoutRef<"div"> {
  sizePreset?: SizePresetKey;
  alignmentPreset?: AlignmentPresetKey;
}

export const SliderValue = forwardRef<HTMLDivElement, Props>(
  function SliderValue(props: Props, propRef: Ref<HTMLDivElement>) {
    const {
      sizePreset = defaultSizePreset,
      alignmentPreset = defaultAlignmentPreset,
      ...htmlDivProps
    } = props

    const [
      hasLabel,
      setHasLabel,
    ] = useState<boolean>(false)

    const [
      unitWidth,
      setUnitWidth,
    ] = useState<number>()

    const [
      sizePresetKey,
      setSizePresetKey,
    ] = useState<SizePresetKey>()

    const [
      max,
      setMax,
    ] = useState<string | number>()

    const [
      placeholder,
      setPlaceholder,
    ] = useState<string | number>()

    const [
      value,
      setValue,
    ] = useState<string | number>()

    const contextValue = useMemo(
      () => ({
        hasLabel,
        max,
        placeholder,
        setHasLabel,
        setMax,
        setPlaceholder,
        setSizePresetKey,
        setUnitWidth,
        setValue,
        sizePresetKey,
        unitWidth,
        value,
      }),
      [
        hasLabel,
        max,
        placeholder,
        setHasLabel,
        setMax,
        setPlaceholder,
        setSizePresetKey,
        setUnitWidth,
        setValue,
        sizePresetKey,
        unitWidth,
        value,
      ],
    )

    const className = classNames(
      props.className,
      styles.sliderValue,
      styles[AlignmentPreset[alignmentPreset]],
      styles[SizePreset[sizePreset]],
      {
        [styles.sliderValueUnset]: value === undefined || value === "",
      },
    )

    return (
      <SliderValueContext.Provider value={contextValue}>
        <div
          {...htmlDivProps}
          className={className}
          ref={propRef}
        />
      </SliderValueContext.Provider>
    )
  },
)
